import { ReactNode } from "react";
import styled from "styled-components";
import { colors } from "../app/styles";

interface ModuleCardProps {
  icon?: ReactNode;
  title: string;
  description: string;
  count?: number;
  /** Amber call-to-action shown under the description (e.g. "Exit inward"). */
  notice?: string;
  disabled?: boolean;
  onClick?: () => void;
}

const Card = styled.button<{ $disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 88px;
  padding: 16px;
  border: 1px solid ${colors.cardBorder};
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.06);
  text-align: left;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  transition: transform 0.08s ease;

  &:active {
    transform: ${(p) => (p.$disabled ? "none" : "scale(0.99)")};
  }
`;

const IconWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: 48px;
  border-radius: 12px;
  background: #e0fbee;

  svg {
    height: 28px;
    width: 28px;
  }
`;

const Body = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 17px;
  color: ${colors.primaryDark};
`;

const Description = styled.div`
  font-size: 13px;
  color: ${colors.textMuted};
  margin-top: 2px;
`;

const CountBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 12px;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 12px;
  background: #ff4d4f;
  color: #ffffff;
  font-weight: 700;
  font-size: 13px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
`;

const Notice = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${colors.warnBg};
  border: 1px solid ${colors.warnBorder};
  color: ${colors.warnText};
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${colors.warnBorder};
  }
`;

export default function ModuleCard({
  icon,
  title,
  description,
  count,
  notice,
  disabled,
  onClick,
}: ModuleCardProps) {
  return (
    <Card
      type="button"
      $disabled={disabled}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      {icon && <IconWrap>{icon}</IconWrap>}
      <Body>
        <Title>{title}</Title>
        <Description>{description}</Description>
        {notice && <Notice>{notice}</Notice>}
      </Body>
      {typeof count === "number" && count > 0 && (
        <CountBadge>{count}</CountBadge>
      )}
    </Card>
  );
}
